import axios from "axios";
import {
  getAccessToken,
  setAccessToken,
  clearTokens,
} from "../store/authStore";
import { extractAccessToken } from "../utils/tokens";

const _raw = import.meta.env.VITE_API_BASE_URL ?? "";
const BASE_URL =
  _raw && !_raw.startsWith("http") && !_raw.startsWith("/")
    ? `https://${_raw}`
    : _raw;

const AUTH_BASE_URL = BASE_URL.replace(/\/api\/?$/, "");

const NO_REFRESH_PATHS = [
  "/auth/magic-link",
  "/auth/google",
  "/auth/admin/login",
  "/auth/admin/login/verify-otp",
];

const unauthorizedCallbacks = new Set();

export function onUnauthorized(cb) {
  unauthorizedCallbacks.add(cb);
  return () => unauthorizedCallbacks.delete(cb);
}

function notifyUnauthorized() {
  unauthorizedCallbacks.forEach((cb) => cb());
}

function shouldSkipRefresh(url = "") {
  return NO_REFRESH_PATHS.some((path) => url.includes(path));
}

let isRefreshing = false;
let failedQueue = [];

function processQueue(error, token = null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
}

const apiClient = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

apiClient.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    if (
      status !== 401 ||
      !originalRequest ||
      originalRequest._retry ||
      shouldSkipRefresh(originalRequest.url)
    ) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        if (token) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
        }
        return apiClient(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const { data } = await axios.post(
        `${AUTH_BASE_URL}/auth/refresh`,
        {},
        { withCredentials: true },
      );

      const accessToken = extractAccessToken(data);
      if (!accessToken) {
        throw new Error("Refresh response contained no access token");
      }

      setAccessToken(accessToken);
      processQueue(null, accessToken);
      originalRequest.headers.Authorization = `Bearer ${accessToken}`;
      return apiClient(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      clearTokens();
      notifyUnauthorized();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export { BASE_URL, AUTH_BASE_URL, notifyUnauthorized };
export default apiClient;